import { useEffect, useState } from 'react';
import { Eye, EyeOff, Mail, Check } from 'lucide-react';
import { signup, resendVerificationEmail } from '../../../lib/funnelAuthClient';
import PasswordRequirementsChecklist from './PasswordRequirementsChecklist';
import {
  fieldInputVariants,
  primaryButtonVariants,
  Spinner,
  ErrorBanner,
} from '../authFormPrimitives';

const RESEND_COOLDOWN_SECONDS = 30;

// Ported from roammyway_funnel's FullNameStep/CreatePasswordStep/
// CheckEmailStep.tsx, combined into one sub-flow like AuthFlow.tsx's own
// SignupSteps. Calls /api/auth/signup directly instead of useSignUpLogic.
export default function SignupSteps({ email, onBack }) {
  const [subStep, setSubStep] = useState('fullName');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullNameError, setFullNameError] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendToken, setResendToken] = useState(null);

  const onContinueFromFullName = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setFullNameError('Full name is required');
      return;
    }
    setFullNameError(null);
    setSubStep('createPassword');
  };

  const onCreatePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { ok, status, data } = await signup({ name: name.trim(), email, password });
      if (!ok) {
        if (status === 409) {
          setError('An account with this email already exists. Please sign in instead.');
        } else if (status === 400) {
          setError(data.error || 'Please check your information and try again.');
        } else if (status === 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(data.error || 'Failed to create account');
        }
        return;
      }

      setResendToken(typeof data.resendToken === 'string' ? data.resendToken : null);
      setSubStep('checkEmail');
    } catch {
      setError('Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div key={subStep} className="animate-auth-surface">
      {subStep === 'fullName' && (
        <FullNameStep
          email={email}
          name={name}
          onChange={(e) => {
            setName(e.target.value);
            if (fullNameError) setFullNameError(null);
          }}
          error={fullNameError}
          onContinue={onContinueFromFullName}
          onBack={onBack}
        />
      )}
      {subStep === 'createPassword' && (
        <CreatePasswordStep
          password={password}
          confirmPassword={confirmPassword}
          onPasswordChange={(e) => setPassword(e.target.value)}
          onConfirmPasswordChange={(e) => setConfirmPassword(e.target.value)}
          loading={loading}
          error={error}
          onSubmit={onCreatePasswordSubmit}
        />
      )}
      {subStep === 'checkEmail' && <CheckEmailStep email={email} resendToken={resendToken} onBack={onBack} />}
    </div>
  );
}

function FullNameStep({ email, name, onChange, error, onContinue, onBack }) {
  return (
    <>
      <h1 className="text-left font-brand text-[26px] font-bold leading-[1.2] tracking-[-0.3px] text-brand-blue lg:text-center lg:text-[28px] lg:tracking-[-0.4px]">
        Let's create your account
      </h1>

      <div className="mt-3 rounded-xl bg-brand-grey px-4 py-2.5 lg:text-center">
        <p className="font-brand text-[11.5px] font-semibold uppercase tracking-wide text-brand-text-secondary">
          Your email
        </p>
        <p className="font-brand text-sm text-brand-blue">{email}</p>
      </div>

      {error && (
        <div className="mt-4">
          <ErrorBanner message={error} />
        </div>
      )}

      <form onSubmit={onContinue} className="mt-4 space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor="romaya-signup-name" className="font-brand text-[13px] font-semibold text-brand-blue">
            Full name
          </label>
          <input
            id="romaya-signup-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            aria-invalid={Boolean(error)}
            value={name}
            onChange={onChange}
            placeholder="John Doe"
            className={fieldInputVariants({ invalid: Boolean(error), filled: Boolean(name) })}
          />
        </div>

        <button type="submit" className={primaryButtonVariants({ tone: 'idle' })}>
          Continue
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onBack}
          className="font-brand text-sm font-semibold text-brand-teal-strong transition-colors hover:text-brand-blue focus:outline-none focus-visible:underline"
        >
          Use a different email
        </button>
      </div>
    </>
  );
}

function CreatePasswordStep({ password, confirmPassword, onPasswordChange, onConfirmPasswordChange, loading, error, onSubmit }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <>
      <h1 className="text-left font-brand text-[26px] font-bold leading-[1.2] tracking-[-0.3px] text-brand-blue lg:text-center lg:text-[28px] lg:tracking-[-0.4px]">
        Create your password
      </h1>

      {error && (
        <div className="mt-4">
          <ErrorBanner message={error} />
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-4 space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor="romaya-signup-password" className="font-brand text-[13px] font-semibold text-brand-blue">
            Password
          </label>
          <div className="relative">
            <input
              id="romaya-signup-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={8}
              disabled={loading}
              value={password}
              onChange={onPasswordChange}
              placeholder="At least 8 characters"
              className={`pr-12 ${fieldInputVariants({ filled: Boolean(password) })}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-brand-text-secondary transition-colors hover:text-brand-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal-strong"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
          <PasswordRequirementsChecklist password={password} />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="romaya-signup-confirm-password" className="font-brand text-[13px] font-semibold text-brand-blue">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="romaya-signup-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={8}
              disabled={loading}
              value={confirmPassword}
              onChange={onConfirmPasswordChange}
              placeholder="Repeat your password"
              className={`pr-12 ${fieldInputVariants({ filled: Boolean(confirmPassword) })}`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-brand-text-secondary transition-colors hover:text-brand-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal-strong"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>

        <button type="submit" disabled={loading} className={primaryButtonVariants({ tone: 'idle' })}>
          {loading ? (
            <span className="flex items-center justify-center">
              <Spinner className="-ml-1 mr-2 text-white" />
              Creating account...
            </span>
          ) : (
            'Create account'
          )}
        </button>
      </form>
    </>
  );
}

function CheckEmailStep({ email, resendToken, onBack }) {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [lastResendTime, setLastResendTime] = useState(0);
  const [resendCooldownRemaining, setResendCooldownRemaining] = useState(0);

  useEffect(() => {
    if (lastResendTime === 0) {
      setResendCooldownRemaining(0);
      return;
    }
    const updateCooldown = () => {
      const elapsed = Math.floor((Date.now() - lastResendTime) / 1000);
      setResendCooldownRemaining(Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed));
    };
    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [lastResendTime]);

  const handleResend = async () => {
    if (!resendToken || resendLoading || resendCooldownRemaining > 0) return;
    setResendLoading(true);
    setResendError(null);
    setResendSuccess(false);
    try {
      const { ok, status, data } = await resendVerificationEmail(resendToken);
      if (ok) {
        setResendSuccess(true);
        setLastResendTime(Date.now());
      } else if (status === 429) {
        setResendError(
          data.retryAfter
            ? `Too many requests. Try again in ${Math.ceil(data.retryAfter / 60)} minute(s).`
            : 'Too many requests. Please try again later.',
        );
      } else {
        setResendError(data.error || 'This link has expired. Please sign in again to request a new one.');
      }
    } catch {
      setResendError('Network error. Please check your internet connection and try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="py-2 text-center">
      <div className="relative mb-6 flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-teal-strong/10">
          <Mail className="h-7 w-7 text-brand-teal-strong" aria-hidden="true" strokeWidth={1.75} />
        </div>
        <div className="absolute -bottom-1 right-[calc(50%-38px)] flex h-6 w-6 items-center justify-center rounded-full bg-brand-success ring-2 ring-white">
          <Check className="h-3.5 w-3.5 text-white" aria-hidden="true" strokeWidth={3} />
        </div>
      </div>

      <h1 className="font-brand text-xl font-bold text-brand-blue lg:text-2xl">Check your email</h1>
      <p className="mt-2 font-brand text-sm text-brand-text-secondary">We've sent a verification link to</p>
      <p className="mt-0.5 font-brand text-sm font-semibold text-brand-blue">{email}</p>
      <p className="mx-auto mt-3 max-w-[280px] font-brand text-sm text-brand-text-secondary">
        Open your email and click the verification link to verify your account.
      </p>

      <div className="mt-6 space-y-3">
        {resendSuccess && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-brand-success/30 bg-brand-success/10 px-4 py-2.5 font-brand text-sm text-brand-success">
            <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Verification email sent
          </div>
        )}
        {!resendSuccess && resendError && <ErrorBanner message={resendError} />}

        <button
          type="button"
          onClick={handleResend}
          disabled={resendLoading || !resendToken || resendCooldownRemaining > 0}
          className={primaryButtonVariants({ tone: 'idle' })}
        >
          {resendLoading ? (
            <span className="flex items-center justify-center">
              <Spinner className="-ml-1 mr-2 text-white" />
              Sending...
            </span>
          ) : resendCooldownRemaining > 0 ? (
            `Resend in ${resendCooldownRemaining}s`
          ) : (
            'Resend verification email'
          )}
        </button>

        <button
          type="button"
          onClick={onBack}
          className="font-brand text-sm font-semibold text-brand-text-secondary transition-colors hover:text-brand-blue focus:outline-none focus-visible:underline"
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}
